import { keepPreviousData, useQuery } from "@tanstack/preact-query"
import { useState } from "react"
import { useLocation } from "wouter"

import { Badge } from "@/components/ui/badge"
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"

import type { SessionSummary } from "../../../server/routes/sessions"
import { api } from "../../api"
import { fmtRelative, fmtTokens } from "../../format"

const PAGE_SIZE = 50

export function SessionsPage() {
  const [cursors, setCursors] = useState<number[]>([Infinity])
  const [, navigate] = useLocation()
  const page = cursors.length - 1
  const { data, isPending, isPlaceholderData } = useQuery<SessionSummary[]>({
    queryKey: ["sessions", cursors[page]],
    queryFn: () =>
      api(`/sessions?limit=${PAGE_SIZE}&before=${cursors[page]}`).then((r) => r.json()),
    placeholderData: keepPreviousData,
  })
  const sessions = data ?? []
  const loading = isPending || isPlaceholderData
  const hasMore = sessions.length === PAGE_SIZE

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Session</TableHead>
            <TableHead>Models</TableHead>
            <TableHead className="text-right">Requests</TableHead>
            <TableHead className="text-right">Tokens</TableHead>
            <TableHead className="text-right">Last Active</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isPending
            ? [...Array(5)].map((_, i) => (
                <TableRow key={i}>
                  <TableCell colSpan={5}>
                    <Skeleton className="h-5 w-full" />
                  </TableCell>
                </TableRow>
              ))
            : sessions.map((s) => (
                <TableRow
                  key={s.sessionId}
                  className="cursor-pointer"
                  onClick={() => navigate(`/sessions/${s.sessionId}`)}
                >
                  <TableCell className="max-w-40 truncate font-mono">{s.sessionId}</TableCell>
                  <TableCell>
                    {s.models.slice(0, 3).map((m) => (
                      <Badge key={m} variant="secondary" className="mr-1">
                        {m}
                      </Badge>
                    ))}
                    {s.models.length > 3 && <Badge variant="outline">+{s.models.length - 3}</Badge>}
                  </TableCell>
                  <TableCell className="text-right">{s.count}</TableCell>
                  <TableCell className="text-right">
                    {fmtTokens(s.usage.input + s.usage.output + s.usage.cacheRead)}
                  </TableCell>
                  <TableCell className="text-right" title={new Date(s.lastTime).toLocaleString()}>
                    {fmtRelative(s.lastTime)}
                  </TableCell>
                </TableRow>
              ))}
        </TableBody>
      </Table>
      <Pagination className="mt-1 justify-end">
        <PaginationContent>
          <PaginationItem>
            <PaginationPrevious
              aria-disabled={loading || page === 0}
              className={loading || page === 0 ? "pointer-events-none opacity-50" : undefined}
              onClick={() => setCursors(cursors.slice(0, -1))}
            />
          </PaginationItem>
          <PaginationItem className="text-sm text-muted-foreground px-1">{page + 1}</PaginationItem>
          <PaginationItem>
            <PaginationNext
              aria-disabled={loading || !hasMore}
              className={loading || !hasMore ? "pointer-events-none opacity-50" : undefined}
              onClick={() => setCursors([...cursors, sessions[sessions.length - 1].lastTime])}
            />
          </PaginationItem>
        </PaginationContent>
      </Pagination>
    </>
  )
}
